import React, { useState, useEffect, useCallback } from "react";
import {
  Zap,
  Activity,
  Server,
  RefreshCw,
  Check,
  X,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Cpu,
  Coins,
  ClipboardCheck,
  ExternalLink,
} from "lucide-react";
import { Link } from "react-router-dom";
import api from "../api/client";

/* ── helpers ──────────────────────────────────────────────── */

function fmtDateTime(v) {
  if (!v) return "—";
  try {
    const d = new Date(v);
    if (isNaN(d.getTime())) return String(v);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(v);
  }
}

function fmtTime(v) {
  if (!v) return "—";
  try {
    const d = new Date(v);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "—";
  }
}

function fmtUptime(ms) {
  if (!ms) return "—";
  const secs = Math.floor((Date.now() - ms) / 1000);
  if (secs < 0) return "—";
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

const CATEGORY_ORDER = [
  "Core GSC",
  "Audits",
  "Publishing",
  "Maintenance",
  "Other",
  "Disabled",
];

/* ── small components ─────────────────────────────────────── */

function Toast({ message, type = "success", onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div
      className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium flex items-center gap-2 ${
        type === "success"
          ? "bg-green-900/90 text-green-200 border border-green-700"
          : type === "error"
          ? "bg-red-900/90 text-red-200 border border-red-700"
          : "bg-blue-900/90 text-blue-200 border border-blue-700"
      }`}
    >
      {type === "success" && <Check size={14} />}
      {type === "error" && <X size={14} />}
      {type === "info" && <RefreshCw size={14} />}
      {message}
    </div>
  );
}

function Switch({ checked, disabled, onChange }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onChange}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
        checked ? "bg-copper" : "bg-dark-50"
      } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          checked ? "translate-x-4" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}

/* ── status banner ────────────────────────────────────────── */

function StatusBanner({ status }) {
  const alive = status?.beat_alive;
  // Find the nearest upcoming enabled task for the "next" hint.
  let nextTask = null;
  let nextTime = null;
  if (status?.tasks) {
    for (const t of status.tasks) {
      if (!t.enabled || !t.next_run) continue;
      const ts = new Date(t.next_run).getTime();
      if (isNaN(ts)) continue;
      if (nextTime === null || ts < nextTime) {
        nextTime = ts;
        nextTask = t;
      }
    }
  }
  if (alive) {
    return (
      <div className="flex items-center gap-3 rounded-xl border border-green-700/50 bg-green-900/20 px-5 py-4">
        <span className="relative flex h-3 w-3">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
          <span className="relative inline-flex h-3 w-3 rounded-full bg-green-500" />
        </span>
        <div className="text-sm text-green-200 font-medium">
          Scheduler running
          {nextTask && (
            <span className="text-green-300/80 font-normal">
              {" "}
              — next: <span className="font-mono">{nextTask.name}</span> at{" "}
              {fmtTime(nextTask.next_run)}
            </span>
          )}
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3 rounded-xl border border-red-700/50 bg-red-900/20 px-5 py-4">
      <AlertTriangle size={18} className="text-red-400" />
      <div className="text-sm text-red-200 font-medium">
        Scheduler DOWN
        {status?.beat_down_since && (
          <span className="text-red-300/80 font-normal">
            {" "}
            — last activity {fmtDateTime(status.beat_down_since)}
          </span>
        )}
      </div>
    </div>
  );
}

/* ── workers strip ────────────────────────────────────────── */

function WorkerCard({ worker }) {
  const online = worker.status === "online";
  return (
    <div className="bg-dark-300 border border-dark-50 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-mono text-gray-300 truncate">
          {worker.name.replace("onesuiteseo-", "")}
        </span>
        <span
          className={`flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full ${
            online
              ? "bg-green-900/40 text-green-300 border border-green-700/50"
              : "bg-red-900/40 text-red-300 border border-red-700/50"
          }`}
        >
          <span
            className={`h-1.5 w-1.5 rounded-full ${
              online ? "bg-green-400" : "bg-red-400"
            }`}
          />
          {worker.status}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div>
          <div className="text-sm font-mono font-bold text-gray-200">
            {worker.memory_mb}
          </div>
          <div className="text-[10px] text-gray-500">MB</div>
        </div>
        <div>
          <div className="text-sm font-mono font-bold text-gray-200">
            {worker.restarts}
          </div>
          <div className="text-[10px] text-gray-500">restarts</div>
        </div>
        <div>
          <div className="text-sm font-mono font-bold text-gray-200">
            {fmtUptime(worker.uptime)}
          </div>
          <div className="text-[10px] text-gray-500">uptime</div>
        </div>
      </div>
    </div>
  );
}

/* ── suggested-fix review cards ───────────────────────────── */

function ReviewCard({ site }) {
  const suggested = site.suggested_count || 0;
  const pending = site.pending_count || 0;
  return (
    <div className="bg-dark-300 border border-dark-50 rounded-xl p-5">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-sm font-medium text-gray-200">{site.site}</div>
          <div className="text-[11px] text-gray-500">{site.domain}</div>
        </div>
        <ClipboardCheck size={16} className="text-copper mt-0.5" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Link
          to="/seo-optimizer"
          className="group rounded-lg border border-dark-50 bg-dark-50/20 px-3 py-3 hover:border-copper/40 transition-colors"
        >
          <div
            className={`text-2xl font-mono font-bold ${
              suggested > 0 ? "text-copper" : "text-gray-500"
            }`}
          >
            {suggested.toLocaleString()}
          </div>
          <div className="text-[11px] text-gray-500 flex items-center gap-1">
            awaiting review
            <ExternalLink
              size={10}
              className="text-gray-600 group-hover:text-copper transition-colors"
            />
          </div>
        </Link>
        <div className="rounded-lg border border-dark-50 bg-dark-50/20 px-3 py-3">
          <div
            className={`text-2xl font-mono font-bold ${
              pending > 0 ? "text-amber-400" : "text-gray-500"
            }`}
          >
            {pending.toLocaleString()}
          </div>
          <div className="text-[11px] text-gray-500">in WP panel</div>
        </div>
      </div>
    </div>
  );
}

/* ── token budget cards ───────────────────────────────────── */

function TokenCard({ site }) {
  const [open, setOpen] = useState(false);
  const pct = typeof site.percent === "number" ? site.percent : null;
  const barColor =
    pct === null
      ? "bg-gray-600"
      : pct > 90
      ? "bg-red-500"
      : pct >= 70
      ? "bg-amber-500"
      : "bg-green-500";

  return (
    <div className="bg-dark-300 border border-dark-50 rounded-xl p-5">
      <div className="flex items-start justify-between mb-1">
        <div>
          <div className="text-sm font-medium text-gray-200">{site.site}</div>
          <div className="text-[11px] text-gray-500">{site.domain}</div>
        </div>
        <Coins size={16} className="text-copper mt-0.5" />
      </div>

      {site.error ? (
        <div className="mt-3 flex items-center gap-2 text-xs text-red-300 bg-red-900/20 border border-red-800/40 rounded-lg px-3 py-2">
          <AlertTriangle size={12} />
          {site.error}
        </div>
      ) : (
        <>
          <div className="mt-3 flex justify-between text-[11px] text-gray-400 mb-1 font-mono">
            <span>
              {site.tokens_used != null
                ? Number(site.tokens_used).toLocaleString()
                : "—"}{" "}
              / {site.budget != null ? Number(site.budget).toLocaleString() : "—"}
            </span>
            <span>{pct != null ? `${pct}%` : "—"}</span>
          </div>
          <div className="w-full bg-dark-50 rounded-full h-2">
            <div
              className={`${barColor} rounded-full h-2 transition-all`}
              style={{ width: `${pct != null ? Math.min(pct, 100) : 0}%` }}
            />
          </div>
          <div className="mt-2 flex justify-between items-center text-[11px] text-gray-500">
            <span>
              {site.cost != null && (
                <>
                  ${Number(site.cost).toFixed(2)}
                  {site.cost_budget != null && ` / $${site.cost_budget}`}
                  {site.month && ` · ${site.month}`}
                </>
              )}
            </span>
            {site.recent && site.recent.length > 0 && (
              <button
                onClick={() => setOpen((o) => !o)}
                className="flex items-center gap-1 text-gray-400 hover:text-copper transition-colors"
              >
                {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                {site.recent.length} recent
              </button>
            )}
          </div>

          {open && site.recent && site.recent.length > 0 && (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="text-gray-500 border-b border-dark-50">
                    <th className="text-left py-1 pr-2 font-medium">Action</th>
                    <th className="text-left py-1 pr-2 font-medium">Page</th>
                    <th className="text-right py-1 pr-2 font-medium">Tokens</th>
                    <th className="text-left py-1 font-medium">When</th>
                  </tr>
                </thead>
                <tbody>
                  {site.recent.map((r, i) => (
                    <tr key={r.id || i} className="border-b border-dark-50/40">
                      <td className="py-1 pr-2 text-gray-300 font-mono">
                        {r.action_type || "—"}
                      </td>
                      <td className="py-1 pr-2 text-gray-400 truncate max-w-[140px]">
                        {r.page_title || "—"}
                      </td>
                      <td className="py-1 pr-2 text-right text-gray-400 font-mono">
                        {r.tokens_used != null
                          ? Number(r.tokens_used).toLocaleString()
                          : "—"}
                      </td>
                      <td className="py-1 text-gray-500">
                        {fmtDateTime(r.created_at || r.applied_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ── tasks table (grouped) ────────────────────────────────── */

function TaskGroup({ category, tasks, onToggle, togglingIds, collapsedInit }) {
  const [collapsed, setCollapsed] = useState(!!collapsedInit);
  return (
    <div className="bg-dark-300 border border-dark-50 rounded-xl overflow-hidden">
      <button
        onClick={() => setCollapsed((c) => !c)}
        className="w-full flex items-center justify-between px-5 py-3 hover:bg-dark-50/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          {collapsed ? (
            <ChevronDown size={14} className="text-gray-500" />
          ) : (
            <ChevronUp size={14} className="text-gray-500" />
          )}
          <span className="text-sm font-semibold text-gray-200">{category}</span>
          <span className="text-[11px] text-gray-500 bg-dark-50 rounded-full px-2 py-0.5">
            {tasks.length}
          </span>
        </div>
      </button>
      {!collapsed && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="text-gray-500 text-xs border-t border-dark-50">
                <th className="text-left px-5 py-2 font-medium">Task</th>
                <th className="text-left px-3 py-2 font-medium">Schedule</th>
                <th className="text-left px-3 py-2 font-medium">Last run</th>
                <th className="text-left px-3 py-2 font-medium">Next run</th>
                <th className="text-right px-5 py-2 font-medium">Enabled</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((t) => (
                <tr
                  key={t.id}
                  className="border-t border-dark-50/40 hover:bg-dark-50/20"
                >
                  <td className="px-5 py-2.5">
                    <div className="text-gray-200">{t.name}</div>
                    <div className="text-[10px] text-gray-600 font-mono truncate max-w-[240px]">
                      {t.task}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-gray-400 font-mono text-xs">
                    {t.schedule}
                  </td>
                  <td className="px-3 py-2.5 text-gray-500 text-xs">
                    {fmtDateTime(t.last_run_at)}
                  </td>
                  <td className="px-3 py-2.5 text-gray-500 text-xs">
                    {t.enabled ? fmtDateTime(t.next_run) : "—"}
                  </td>
                  <td className="px-5 py-2.5 text-right">
                    <Switch
                      checked={t.enabled}
                      disabled={togglingIds.has(t.id)}
                      onChange={() => onToggle(t)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ── main ─────────────────────────────────────────────────── */

export default function Automation() {
  const [status, setStatus] = useState(null);
  const [usage, setUsage] = useState(null);
  const [fixes, setFixes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [usageLoading, setUsageLoading] = useState(true);
  const [fixesLoading, setFixesLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [togglingIds, setTogglingIds] = useState(new Set());
  const [toast, setToast] = useState(null);

  const fetchStatus = useCallback(async () => {
    const res = await api.get("/automation/status/");
    setStatus(res.data);
  }, []);

  const fetchUsage = useCallback(async () => {
    setUsageLoading(true);
    try {
      const res = await api.get("/automation/token-usage/");
      setUsage(res.data);
    } catch {
      setUsage([]);
    } finally {
      setUsageLoading(false);
    }
  }, []);

  const fetchFixes = useCallback(async () => {
    setFixesLoading(true);
    try {
      const res = await api.get("/automation/suggested-fixes/");
      setFixes(res.data);
    } catch {
      setFixes([]);
    } finally {
      setFixesLoading(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchStatus()
      .catch(() => setStatus(null))
      .finally(() => setLoading(false));
    fetchUsage();
    fetchFixes();
  }, [fetchStatus, fetchUsage, fetchFixes]);

  const refreshAll = async () => {
    setRefreshing(true);
    await Promise.allSettled([fetchStatus(), fetchUsage(), fetchFixes()]);
    setRefreshing(false);
  };

  // Pending WP-agent-log rows per domain, derived from the token-usage payload.
  const pendingByDomain = {};
  (usage || []).forEach((s) => {
    pendingByDomain[s.domain] = (s.recent || []).filter(
      (r) => r.status === "pending"
    ).length;
  });
  const reviewSites = (fixes || []).map((f) => ({
    ...f,
    pending_count: pendingByDomain[f.domain] || 0,
  }));

  const handleToggle = async (task) => {
    setTogglingIds((prev) => new Set(prev).add(task.id));
    // Optimistic update
    setStatus((prev) =>
      prev
        ? {
            ...prev,
            tasks: prev.tasks.map((t) =>
              t.id === task.id ? { ...t, enabled: !t.enabled } : t
            ),
          }
        : prev
    );
    try {
      await api.post(`/automation/tasks/${task.id}/toggle/`);
      await fetchStatus();
      setToast({
        message: `${task.name} ${!task.enabled ? "enabled" : "disabled"}`,
        type: "success",
      });
    } catch {
      // Revert on failure
      await fetchStatus();
      setToast({ message: "Toggle failed", type: "error" });
    } finally {
      setTogglingIds((prev) => {
        const n = new Set(prev);
        n.delete(task.id);
        return n;
      });
    }
  };

  if (loading) {
    return (
      <div className="p-8 space-y-6">
        <div className="h-8 w-48 bg-dark-300 rounded animate-pulse" />
        <div className="h-16 bg-dark-300 rounded-xl animate-pulse" />
        <div className="grid grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-28 bg-dark-300 rounded-xl animate-pulse" />
          ))}
        </div>
        <div className="h-64 bg-dark-300 rounded-xl animate-pulse" />
      </div>
    );
  }

  // Group tasks by category, preserving CATEGORY_ORDER
  const grouped = {};
  (status?.tasks || []).forEach((t) => {
    (grouped[t.category] = grouped[t.category] || []).push(t);
  });
  const orderedCats = [
    ...CATEGORY_ORDER.filter((c) => grouped[c]),
    ...Object.keys(grouped).filter((c) => !CATEGORY_ORDER.includes(c)),
  ];

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Zap size={22} className="text-copper" />
          <h1 className="text-xl font-bold text-white">Automation</h1>
        </div>
        <button
          onClick={refreshAll}
          disabled={refreshing}
          className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-400 hover:text-copper border border-dark-50 rounded-lg transition-colors disabled:opacity-50"
        >
          <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {/* Status banner */}
      <StatusBanner status={status} />

      {/* Workers strip */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Server size={15} className="text-gray-400" />
          <h2 className="text-sm font-semibold text-gray-300">Workers</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {(status?.workers || []).length === 0 ? (
            <div className="col-span-4 text-sm text-gray-500 bg-dark-300 border border-dark-50 rounded-xl px-5 py-4">
              No pm2 worker data available.
            </div>
          ) : (
            (status?.workers || []).map((w) => (
              <WorkerCard key={w.name} worker={w} />
            ))
          )}
        </div>
      </div>

      {/* Suggested fixes awaiting review */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <ClipboardCheck size={15} className="text-gray-400" />
          <h2 className="text-sm font-semibold text-gray-300">
            Suggested fixes awaiting review
          </h2>
        </div>
        {fixesLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-28 bg-dark-300 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : reviewSites.length === 0 ? (
          <div className="text-sm text-gray-500 bg-dark-300 border border-dark-50 rounded-xl px-5 py-4">
            No active sites.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {reviewSites.map((s) => (
              <ReviewCard key={s.domain} site={s} />
            ))}
          </div>
        )}
      </div>

      {/* Token budgets */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Cpu size={15} className="text-gray-400" />
          <h2 className="text-sm font-semibold text-gray-300">AI Token Budgets</h2>
        </div>
        {usageLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-32 bg-dark-300 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : (usage || []).length === 0 ? (
          <div className="text-sm text-gray-500 bg-dark-300 border border-dark-50 rounded-xl px-5 py-4">
            No sites with the AI agent configured.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {usage.map((s) => (
              <TokenCard key={s.domain} site={s} />
            ))}
          </div>
        )}
      </div>

      {/* Tasks */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Activity size={15} className="text-gray-400" />
          <h2 className="text-sm font-semibold text-gray-300">
            Scheduled Tasks
          </h2>
        </div>
        <div className="space-y-3">
          {orderedCats.map((cat) => (
            <TaskGroup
              key={cat}
              category={cat}
              tasks={grouped[cat]}
              onToggle={handleToggle}
              togglingIds={togglingIds}
              collapsedInit={cat === "Disabled"}
            />
          ))}
        </div>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
